import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Storefront, MapPin, ChatCircle } from "@phosphor-icons/react";
import { track } from "@/lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PREVIEW_COUNT = 6;

// Chrome and Firefox cannot decode HEIC/HEIF at all. Unlike a missing file, an
// undecodable one fires no error event either — the <img> just sits there empty
// forever (verified against the one HEIC in storage) — so onError alone can
// never catch it. Treat those as broken up front. Safari does decode them, so
// it still gets the real photo, with onError as its safety net.
const UNDECODABLE = /\.(heic|heif)(\?|$)/i;
const isSafari =
  typeof navigator !== "undefined" &&
  /^((?!chrome|chromium|android|crios|fxios|edg|opr).)*safari/i.test(navigator.userAgent);
const cannotRender = (url) => UNDECODABLE.test(url || "") && !isSafari;

// A spot's cover image. When the stored file has gone missing the storefront
// glyph stands in at the same 4:3 size, so a broken image never reaches the
// viewer as the browser's broken-image icon. A spot that never had an image
// still renders nothing — only broken ones fall back.
export const SpotImage = ({ spot, className = "", glyphSize = 32 }) => {
  const [failed, setFailed] = useState(false);
  if (!spot.image_url) return null;
  if (failed || cannotRender(spot.image_url)) {
    return (
      <div
        className={`w-full aspect-[4/3] bg-[#F0EFEB] flex items-center justify-center ${className}`}
        data-testid="spot-image-fallback"
      >
        <Storefront size={glyphSize} className="text-[#C9C5BA]" />
      </div>
    );
  }
  return (
    <img
      src={`${BACKEND_URL}${spot.image_url}`}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={`w-full aspect-[4/3] object-cover ${className}`}
    />
  );
};

// One spot, as a card. Kept in this file (and exported) so the full page renders
// exactly the same card as the feed strip — the two can't drift apart.
export const SpotCard = ({ spot, sizeLabelFor, onOpen, className = "" }) => {
  const Wrapper = onOpen ? "button" : "div";
  const unit = sizeLabelFor ? sizeLabelFor(spot) : spot.kind === "shoes" ? "UK" : "ft";
  const sizes = (spot.sizes || []).join(", ");
  const place = [spot.area, spot.city].filter(Boolean).join(", ");
  return (
    <Wrapper
      type={onOpen ? "button" : undefined}
      onClick={onOpen}
      className={`bg-white border border-[#E4E1D9] rounded-md overflow-hidden text-left w-full ${
        onOpen ? "hover:border-[#C9C5BA] transition-colors" : ""
      } ${className}`}
      data-testid="offline-spot-card"
    >
      <SpotImage spot={spot} />
      <div className="p-3">
        <h3
          className="text-lg tracking-tight break-words"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {spot.store_name}
        </h3>
        {place && (
          <p className="mt-1 flex items-start gap-1.5 text-xs text-[#6B6A68]">
            <MapPin size={14} className="mt-[1px] shrink-0" />
            <span className="break-words">{place}</span>
          </p>
        )}
        {sizes && (
          <span
            className="inline-block mt-3 text-[10px] uppercase tracking-[0.12em] bg-[#F0EFEB] text-[#2D302A] px-2 py-1 rounded-sm"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
            data-testid="offline-spot-size"
          >
            {spot.kind === "shoes" ? `${unit} ${sizes}` : `${sizes} ${unit}`}
          </span>
        )}
        {spot.notes && (
          <p className="mt-3 text-sm text-[#4A4A48] line-clamp-3">{spot.notes}</p>
        )}
        <div className="mt-3 flex items-center justify-between gap-2">
          {spot.user_name ? (
            <p
              className="text-[11px] text-[#9A9A98]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              shared by {spot.user_name}
            </p>
          ) : <span />}
          <span
            className="flex items-center gap-1 text-[11px] text-[#9A9A98] shrink-0"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
            data-testid="offline-spot-comment-count"
          >
            <ChatCircle size={12} /> {spot.comment_count || 0}
          </span>
        </div>
      </div>
    </Wrapper>
  );
};

// Strip thumbnail. Falls back to the storefront glyph both when a spot has no
// image and when the one it has fails to load (a stored path whose file is
// gone), so the row never shows a broken-image icon.
const SpotThumb = ({ spot }) => {
  const [failed, setFailed] = useState(false);
  if (!spot.image_url || failed || cannotRender(spot.image_url)) {
    return (
      <span className="w-11 h-11 rounded shrink-0 bg-[#F0EFEB] flex items-center justify-center">
        <Storefront size={18} className="text-[#6B6A68]" />
      </span>
    );
  }
  return (
    <img
      src={`${BACKEND_URL}${spot.image_url}`}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="w-11 h-11 rounded object-cover shrink-0"
    />
  );
};

// "Where to buy in person" — the compact feed strip.
//
// Deliberately the same shape as the Story strip: one row of small
// thumbnail-and-two-lines cards. The feature keeps its place on the feed
// without taking a full section of it — the full grid now lives on its own
// nav destination at /offline-spots.
//
// Scoped to whatever tab the feed is on: a category tab shows only that tab's
// spots, and "All" shows every one. The tab a spot belongs to is decided
// server-side from the sharer's gender and what they bought, so this component
// only has to pass the tab id.
const OfflineSpotsSection = ({ categoryId = "all", categories = [], user, onShare }) => {
  // A shop two thousand kilometres away is noise, so the viewer's own city is
  // the default scope. No city on the profile means no filter — everything shows.
  const city = (user?.city || "").trim();
  const navigate = useNavigate();
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSpots = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/offline-spots`, {
        params: { category_id: categoryId || "all", limit: PREVIEW_COUNT, city: city || undefined },
      })
      .then((res) => setSpots(res.data?.items || []))
      .catch(() => setSpots([]))
      .finally(() => setLoading(false));
  }, [categoryId, city]);

  useEffect(fetchSpots, [fetchSpots]);

  const sizeChipFor = (spot) => {
    const cat = categories.find((c) => c.id === spot.category_id);
    const unit = (cat?.size_label || (spot.kind === "shoes" ? "UK" : "ft")).trim();
    const sizes = (spot.sizes || []).join(", ");
    if (!sizes) return "";
    return spot.kind === "shoes" ? `${unit} ${sizes}` : `${sizes} ${unit}`;
  };

  const share = () => {
    track("offline_spot_share_click", { source: "feed_section", is_logged_in: !!user });
    onShare?.();
  };

  if (loading) return null;

  return (
    <div className="mb-6 md:mb-10" data-testid="offline-spots-section">
      <div className="mb-2.5">
        <p
          className="text-[10px] uppercase tracking-[0.3em] text-[#6B6A68] truncate"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
        >
          Offline spots
          {city && (
            <span className="text-[#9A9A98] normal-case tracking-normal" data-testid="offline-spots-city-scope">
              {" "}· {city}
            </span>
          )}
        </p>
      </div>

      {spots.length === 0 ? (
        // Nothing to show: still invite the first one, but keep it to a single
        // line so an empty strip never takes more room than a full one.
        <button
          type="button"
          onClick={share}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 border border-dashed border-[#E4E1D9] rounded-md text-left hover:border-[#C9C5BA] transition-colors"
          data-testid="offline-spots-empty"
        >
          <Storefront size={18} className="text-[#6B6A68] shrink-0" />
          <span className="text-xs text-[#6B6A68] truncate">
            {city
              ? `No spots in ${city} yet. Know a store there where things fit?`
              : "Know a store where things actually fit? Add the first one."}
          </span>
        </button>
      ) : (
        <div
          className="flex gap-2 overflow-x-auto no-scrollbar snap-x pb-1"
          data-testid="offline-spots-strip"
        >
          {spots.map((s) => {
            const place = [s.area, s.city].filter(Boolean).join(", ");
            const chip = sizeChipFor(s);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  track("offline_spot_open", { spot_id: s.id, source: "feed_section" });
                  navigate(`/offline-spots/${s.id}`);
                }}
                className="flex items-start gap-2 w-72 shrink-0 snap-start text-left bg-white border border-[#E4E1D9] rounded-md p-2 hover:border-[#C9C5BA] transition-colors"
                data-testid="offline-spot-card"
              >
                <SpotThumb spot={s} />
                {/* Both lines truncate: a wrapping name or place made the
                    card grow taller than its neighbours and broke the row.
                    The note and the full text live on the spot's own page and
                    on the /offline-spots grid. */}
                <span className="min-w-0 flex-1">
                  <span className="flex items-start gap-2">
                    <span className="flex-1 min-w-0 text-xs text-[#2D302A] truncate">{s.store_name}</span>
                    {chip && (
                      <span
                        className="shrink-0 text-[9px] uppercase tracking-[0.1em] text-[#2D302A] bg-[#F0EFEB] px-1.5 py-1 rounded-sm"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                        data-testid="offline-spot-size"
                      >
                        {chip}
                      </span>
                    )}
                  </span>
                  <span className="block mt-0.5 text-xs text-[#6B6A68] truncate">
                    {place || "Offline spot"}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OfflineSpotsSection;
